import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/errorHandling';
import AmountInput from '../ui/AmountInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import {
  financialReportsApi,
  FinancialReport,
  ReportType,
  ReportPeriod,
  ReportLineItem,
  CreateFinancialReportDto,
} from '../../api/financialReports';
import { costCentersApi, CostCenter } from '../../api/costCenters';

const today = () => new Date().toISOString().split('T')[0];

const emptyReport: CreateFinancialReportDto = {
  title: '',
  type: ReportType.CUSTOM,
  period: ReportPeriod.MONTHLY,
  startDate: today(),
  endDate: today(),
  costCenterId: undefined,
  lineItems: [],
  notes: '',
};

const typeLabel = (t: ReportType) =>
  t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function FinancialReportsPanel() {
  const [reports, setReports] = useState<FinancialReport[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<FinancialReport | null>(null);
  const [form, setForm] = useState<CreateFinancialReportDto>(emptyReport);
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState<FinancialReport | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportsData, ccData] = await Promise.all([
        financialReportsApi.getFinancialReports(),
        costCentersApi.getCostCenters().catch(() => [] as CostCenter[]),
      ]);
      setReports(reportsData);
      setCostCenters(ccData);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load financial reports'));
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyReport, lineItems: [{ category: '', description: '', amount: 0 }] });
    setShowModal(true);
  };

  const openEdit = (report: FinancialReport) => {
    setEditing(report);
    setForm({
      title: report.title,
      type: report.type,
      period: report.period,
      startDate: report.startDate?.split('T')[0] ?? today(),
      endDate: report.endDate?.split('T')[0] ?? today(),
      costCenterId: report.costCenterId,
      lineItems: report.lineItems?.length ? report.lineItems : [{ category: '', description: '', amount: 0 }],
      notes: report.notes ?? '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const cleanedLineItems = (form.lineItems || []).filter((li) => li.category.trim() || li.amount);
    const payload: CreateFinancialReportDto = {
      ...form,
      costCenterId: form.costCenterId || undefined,
      lineItems: cleanedLineItems,
    };
    setSaving(true);
    try {
      if (editing) {
        await financialReportsApi.updateFinancialReport(editing.id, payload);
        toast.success('Report updated');
      } else {
        await financialReportsApi.createFinancialReport(payload);
        toast.success('Report created');
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save report'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (report: FinancialReport) => {
    if (!window.confirm(`Delete report "${report.title}"?`)) return;
    try {
      await financialReportsApi.deleteFinancialReport(report.id);
      toast.success('Report deleted');
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete report'));
    }
  };

  const updateLineItem = (index: number, patch: Partial<ReportLineItem>) => {
    setForm((p) => ({
      ...p,
      lineItems: (p.lineItems || []).map((li, i) => (i === index ? { ...li, ...patch } : li)),
    }));
  };

  const addLineItem = () => {
    setForm((p) => ({ ...p, lineItems: [...(p.lineItems || []), { category: '', description: '', amount: 0 }] }));
  };

  const removeLineItem = (index: number) => {
    setForm((p) => ({ ...p, lineItems: (p.lineItems || []).filter((_, i) => i !== index) }));
  };

  const formTotal = useMemo(
    () => (form.lineItems || []).reduce((sum, li) => sum + Number(li.amount || 0), 0),
    [form.lineItems],
  );

  const costCenterName = (id?: string) => costCenters.find((c) => c.id === id)?.name;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="bg-white p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Saved Financial Reports</h3>
          <p className="text-sm text-slate-500">Build and store custom statements with line items</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" /> New Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-12">
          <DocumentChartBarIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-semibold text-slate-900">No saved reports</h3>
          <p className="mt-1 text-sm text-slate-500">Create your first financial report to get started.</p>
        </div>
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cost Center</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                <th className="relative px-4 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-slate-900">{report.title}</div>
                    <div className="text-sm text-slate-500">
                      {format(new Date(report.startDate), 'MMM dd, yyyy')} – {format(new Date(report.endDate), 'MMM dd, yyyy')}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{typeLabel(report.type)}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{report.period.charAt(0) + report.period.slice(1).toLowerCase()}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{costCenterName(report.costCenterId) || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">{formatCurrency(Number(report.totalAmount || 0))}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => setViewing(report)} className="text-cyan-600 hover:text-cyan-900">
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => openEdit(report)} className="text-cyan-600 hover:text-cyan-900">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(report)} className="text-red-600 hover:text-red-900">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BrandModal open={showModal} onClose={() => setShowModal(false)} maxWidthClass="max-w-2xl">
        <BrandModalHeader
          title={editing ? 'Edit Report' : 'New Report'}
          onClose={() => setShowModal(false)}
        />
        <BrandModalBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as ReportType }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    {Object.values(ReportType).map((t) => (
                      <option key={t} value={t}>{typeLabel(t)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">Period</label>
                  <select
                    value={form.period}
                    onChange={(e) => setForm((p) => ({ ...p, period: e.target.value as ReportPeriod }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    {Object.values(ReportPeriod).map((pr) => (
                      <option key={pr} value={pr}>{pr.charAt(0) + pr.slice(1).toLowerCase()}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">End Date</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Cost Center (optional)</label>
                <select
                  value={form.costCenterId ?? ''}
                  onChange={(e) => setForm((p) => ({ ...p, costCenterId: e.target.value || undefined }))}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                >
                  <option value="">None</option>
                  {costCenters.map((cc) => (
                    <option key={cc.id} value={cc.id}>{cc.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Line Items</label>
                  <button type="button" onClick={addLineItem} className="text-sm text-cyan-600 hover:text-cyan-800 inline-flex items-center">
                    <PlusIcon className="h-4 w-4 mr-1" /> Add line
                  </button>
                </div>
                <div className="space-y-2">
                  {(form.lineItems || []).map((li, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        placeholder="Category"
                        value={li.category}
                        onChange={(e) => updateLineItem(index, { category: e.target.value })}
                        className="col-span-3 input border-gray-300 shadow-sm sm:text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={li.description}
                        onChange={(e) => updateLineItem(index, { description: e.target.value })}
                        className="col-span-5 input border-gray-300 shadow-sm sm:text-sm"
                      />
                      <div className="col-span-3">
                        <AmountInput
                          placeholder="Amount"
                          value={li.amount}
                          onChange={(v) => updateLineItem(index, { amount: v ?? 0 })}
                          min={0}
                          inputClassName="input border-gray-300 shadow-sm sm:text-sm text-right"
                        />
                      </div>
                      <button type="button" onClick={() => removeLineItem(index)} className="col-span-1 text-red-600 hover:text-red-900 flex justify-center">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-right text-sm font-medium text-slate-900">
                  Total: {formatCurrency(formTotal)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary text-sm">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary text-sm">
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </button>
        </BrandModalFooter>
      </BrandModal>

      <BrandModal open={!!viewing} onClose={() => setViewing(null)} maxWidthClass="max-w-2xl">
        {viewing ? (
          <>
            <BrandModalHeader
              title={viewing.title}
              subtitle={`${typeLabel(viewing.type)} · ${format(new Date(viewing.startDate), 'MMM dd, yyyy')} – ${format(new Date(viewing.endDate), 'MMM dd, yyyy')}`}
              onClose={() => setViewing(null)}
            />
            <BrandModalBody>
            <div className="overflow-hidden border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(viewing.lineItems || []).map((li, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 text-sm text-slate-900">{li.category}</td>
                      <td className="px-3 py-2 text-sm text-slate-500">{li.description}</td>
                      <td className="px-3 py-2 text-sm text-right text-slate-900">{formatCurrency(Number(li.amount || 0))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 text-sm font-medium text-slate-900">Total</td>
                    <td className="px-3 py-2 text-sm text-right font-semibold text-slate-900">{formatCurrency(Number(viewing.totalAmount || 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {viewing.notes && <p className="mt-4 text-sm text-slate-600">{viewing.notes}</p>}
            </BrandModalBody>
            <BrandModalFooter>
              <button type="button" onClick={() => setViewing(null)} className="btn btn-secondary text-sm">Close</button>
            </BrandModalFooter>
          </>
        ) : null}
      </BrandModal>
    </div>
  );
}
